import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import QuantityControl from '../helper/QuantityControl'
import { useUser } from '../context/UserContext';

const CartSection = () => {
    const { cartItems, removeFromCart, addToCart } = useUser();
    const [couponCode, setCouponCode] = useState('');
    const [discount, setDiscount] = useState(0);
    const [shippingCost, setShippingCost] = useState(0);
    const [taxRate] = useState(0.1); // 10% tax

    // Update quantity in cart
    const updateQuantity = (product, newQuantity) => {
        if (newQuantity < 1) return;
        
        // Use addToCart with difference in quantity (wait, addToCart in UserContext adds quantity).
        // Let's rely on event or we can just call an update function. 
        // Actually since UserContext `addToCart` does `newCart[index].quantity += quantity`, 
        // we can find the difference.
        const currentQty = product.quantity || 1;
        const diff = newQuantity - currentQty;
        addToCart(product, diff);
    };

    // Remove item from cart is already covered by removeFromCart(id)
    const handleRemove = (productId) => {
        removeFromCart(productId);
    };

    const calculateSubtotal = () => {
        return cartItems.reduce((total, item) => {
            return total + (item.price * (item.quantity || 1));
        }, 0);
    };

    // Calculate total after discount, shipping, and tax
    const calculateTotal = () => {
        const subtotal = calculateSubtotal();
        const discountAmount = (subtotal * discount) / 100;
        const afterDiscount = subtotal - discountAmount;
        const taxAmount = afterDiscount * taxRate;
        return afterDiscount + shippingCost + taxAmount;
    };

    // Apply coupon
    const applyCoupon = () => {
        // Example coupons - you can expand this
        const coupons = {
            'SAVE10': 10,
            'SAVE20': 20,
            'WELCOME15': 15,
            'FREESHIP': 0 // Free shipping
        };
        
        if (coupons[couponCode.toUpperCase()]) {
            if (couponCode.toUpperCase() === 'FREESHIP') {
                setShippingCost(0);
                setDiscount(0);
                alert('Free shipping applied!');
            } else {
                setDiscount(coupons[couponCode.toUpperCase()]);
                setShippingCost(10); // Standard shipping
                alert(`${couponCode.toUpperCase()} coupon applied! ${discount}% off`);
            }
        } else {
            alert('Invalid coupon code');
        }
    };

    // Format review count
    const formatReviewCount = (count) => {
        if (count >= 1000) {
            return `${(count / 1000).toFixed(1)}k`;
        }
        return count;
    };

    // Loading state removed since UserContext handles it synchronously with localStorage
    // Wait for UserContext to be ready, but cartItems is synchronously available from local storage.

    if (cartItems.length === 0) {
        return (
            <section className="cart py-80">
                <div className="container container-lg">
                    <div className="text-center py-5">
                        <i className="ph ph-shopping-cart" style={{ fontSize: '64px', color: '#ccc' }}></i>
                        <h3 className="mt-4">Your Cart is Empty</h3>
                        <p className="text-gray-500">Looks like you haven't added any items yet!</p>
                        <Link to="/shop" className="btn btn-main rounded-pill px-40 py-12 mt-3">
                            Continue Shopping
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="cart py-80">
            <div className="container container-lg">
                <div className="row gy-4">
                    <div className="col-xl-9 col-lg-8">
                        <div className="cart-table border border-gray-100 rounded-8 px-40 py-48">
                            <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                                <table className="table style-three">
                                    <thead>
                                        <tr>
                                            <th className="h6 mb-0 text-lg fw-bold">Delete</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Product Name</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Price</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Quantity</th>
                                            <th className="h6 mb-0 text-lg fw-bold">Subtotal</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cartItems.map((item) => (
                                            <tr key={item._id}>
                                                <td>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemove(item._id || item.id)}
                                                        className="remove-tr-btn flex-align gap-12 hover-text-danger-600"
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                    >
                                                        <i className="ph ph-x-circle text-2xl d-flex" />
                                                        Remove
                                                    </button>
                                                </td>
                                                <td>
                                                    <div className="table-product d-flex align-items-center gap-24">
                                                        <Link
                                                            to={`/product-details/${item._id}`}
                                                            className="table-product__thumb border border-gray-100 rounded-8 flex-center"
                                                            style={{
                                                                width: '80px',
                                                                height: '80px',
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            <img
                                                                src={item.image || 'assets/images/thumbs/product-two-img1.png'}
                                                                alt={item.name}
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                                onError={(e) => {
                                                                    e.target.src = 'assets/images/thumbs/product-two-img1.png';
                                                                }}
                                                            />
                                                        </Link>
                                                        <div className="table-product__content text-start">
                                                            <h6 className="title text-lg fw-semibold mb-8">
                                                                <Link
                                                                    to={`/product-details/${item._id}`}
                                                                    className="link text-line-2"
                                                                    style={{ textDecoration: 'none' }}
                                                                >
                                                                    {item.name}
                                                                </Link>
                                                            </h6>
                                                            <div className="flex-align gap-16 mb-16">
                                                                <div className="flex-align gap-6">
                                                                    <span className="text-md fw-medium text-warning-600 d-flex">
                                                                        <i className="ph-fill ph-star" />
                                                                    </span>
                                                                    <span className="text-md fw-semibold text-gray-900">
                                                                        {item.rating || 4.7}
                                                                    </span>
                                                                </div>
                                                                <span className="text-sm fw-medium text-gray-200">
                                                                    |
                                                                </span>
                                                                <span className="text-neutral-600 text-sm">
                                                                    {formatReviewCount(item.reviewsCount || 0)} Reviews
                                                                </span>
                                                            </div>
                                                            <div className="flex-align gap-16 flex-wrap">
                                                                <span className="badge bg-light text-dark px-12 py-6 rounded-pill">
                                                                    {item.category || 'Plants'}
                                                                </span>
                                                                <span className="badge bg-light text-dark px-12 py-6 rounded-pill">
                                                                    {item.plantType || 'Indoor'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="text-lg h6 mb-0 fw-semibold">
                                                        ₹{item.price?.toFixed(2) || '0.00'}
                                                    </span>
                                                    {item.originalPrice && item.originalPrice > item.price && (
                                                        <span className='text-gray-400 text-sm fw-semibold text-decoration-line-through d-block'>
                                                            ₹{item.originalPrice.toFixed(2)}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <QuantityControl 
                                                        initialQuantity={item.quantity || 1}
                                                        onQuantityChange={(newQuantity) => updateQuantity(item, newQuantity)}
                                                    />
                                                </td>
                                                <td>
                                                    <span className="text-lg h6 mb-0 fw-semibold">
                                                        ₹{((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex-between flex-wrap gap-16 mt-16">
                                <div className="flex-align gap-16">
                                    <input
                                        type="text"
                                        className="common-input"
                                        placeholder="Coupon Code"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                        style={{ padding: '12px 16px', borderRadius: '8px', border: '1px solid #ddd' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={applyCoupon}
                                        className="btn btn-main py-12 px-24 rounded-8"
                                    >
                                        Apply Coupon
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => window.location.reload()}
                                    className="text-lg text-gray-500 hover-text-main-600"
                                >
                                    Update Cart
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-3 col-lg-4">
                        <div className="cart-sidebar border border-gray-100 rounded-8 px-24 py-40">
                            <h6 className="text-xl mb-32">Cart Totals</h6>
                            <div className="bg-color-three rounded-8 p-24">
                                <div className="mb-32 flex-between gap-8">
                                    <span className="text-gray-900 font-heading-two">Subtotal</span>
                                    <span className="text-gray-900 fw-semibold">₹{calculateSubtotal().toFixed(2)}</span>
                                </div>
                                {discount > 0 && (
                                    <div className="mb-32 flex-between gap-8">
                                        <span className="text-gray-900 font-heading-two">Discount ({discount}%)</span>
                                        <span className="text-success fw-semibold">-₹{((calculateSubtotal() * discount) / 100).toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="mb-32 flex-between gap-8">
                                    <span className="text-gray-900 font-heading-two">Estimated Delivery</span>
                                    <span className="text-gray-900 fw-semibold">
                                        {shippingCost === 0 ? 'Free' : `₹${shippingCost.toFixed(2)}`}
                                    </span>
                                </div>
                                <div className="mb-0 flex-between gap-8">
                                    <span className="text-gray-900 font-heading-two">Estimated Tax (10%)</span>
                                    <span className="text-gray-900 fw-semibold">
                                        ₹{((calculateSubtotal() - ((calculateSubtotal() * discount) / 100)) * taxRate).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                            <div className="bg-color-three rounded-8 p-24 mt-24">
                                <div className="flex-between gap-8">
                                    <span className="text-gray-900 text-xl fw-semibold">Total</span>
                                    <span className="text-gray-900 text-xl fw-semibold">₹{calculateTotal().toFixed(2)}</span>
                                </div>
                            </div>
                            <Link
                                to="/checkout"
                                className="btn btn-main mt-40 py-18 w-100 rounded-8"
                                state={{ cartItems, total: calculateTotal() }}
                            >
                                Proceed to checkout
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .remove-tr-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                .remove-tr-btn:hover {
                    color: #dc2626 !important;
                }
                .table-product__thumb {
                    transition: transform 0.3s ease;
                }
                .table-product__thumb:hover {
                    transform: scale(1.05);
                }
                .common-input:focus {
                    outline: none;
                    border-color: #00a651;
                }
                @media (max-width: 768px) {
                    .cart-table {
                        padding: 20px !important;
                    }
                    .table-product {
                        flex-direction: column;
                        text-align: center;
                    }
                    .table-product__content {
                        text-align: center !important;
                    }
                }
            `}</style>
        </section>
    )
}

export default CartSection;